import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Pagination from '@/components/pagination/Pagination';
import { useAppSelector } from '@/stores/hooks';
import { colors } from '@/theme/colors';
import { useLoading } from '@/context/LoadingContext';
import CommonTitle from '../../components/title/CommonTitle';
import { Images } from '@/assets';
import CommonSearch from '../../components/search/CommonSearch';
import OrderListItem from '../../components/list/OrderList.tsx/OrderListItem';
import ListSorts from '../../components/sorts/ListSorts';
import { getOrderList } from '@/api/order-list/getOrderList';
import { useDispatch } from 'react-redux';
import { order } from '@/stores/slices/order/orderSlice';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { KeywordItemType, ORDER_SORT_TYPE } from '@/api/type/commonTypes';
import { useOrderQuery } from '@/hooks/router-query/useOrderQuery';
import { langPath } from '@/utils/langPath';

const OrderListPage = () => {
  const { t } = useTranslation();
  const keywordTypeData: KeywordItemType[] = [
    {
      title: 'orderNo',
      keyword: 'ORDER_ID',
    },
    {
      title: 'clinic',
      keyword: 'CLINIC_NAME',
    },
    {
      title: 'patient',
      keyword: 'PATIENT_NAME',
    },
    {
      title: 'orderType',
      keyword: 'ORDER_TYPE',
    },
  ];
  const { page } = useParams<{ page?: string }>();
  const initialPage = page ? Number(page) : 1;

  const [searchParams] = useSearchParams();
  const pageFromQuery = Number(searchParams.get('page')) || 1;

  const [currentPage, setCurrentPage] = useState<number>(pageFromQuery);
  // const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedKeywordType, setSelectedKeywordType] =
    useState<KeywordItemType>(keywordTypeData[0]);
  const dispatch = useDispatch();
  const sort = searchParams.get('sort') as ORDER_SORT_TYPE;
  const keywordType = searchParams.get('keywordType');
  const { orderList } = useAppSelector((state) => state.order);

  const { startLoading, stopLoading } = useLoading();
  const { updateQuery } = useOrderQuery();
  const searchValueRef = useRef('');
  const navigate = useNavigate();

  /** 목록 fetch 함수 */
  const fetchOrderList = useCallback(async () => {
    startLoading();
    try {
      const res = await getOrderList({
        keywordType: keywordType ?? 'ORDER_ID',
        keyword: searchValueRef.current,
        page: currentPage,
        size: 10,
        sort: sort ?? 'ORDER_DATE_DESC',
      });
      if (res.isSuccess) {
        dispatch(order.setOrderList(res.data));
      }
    } finally {
      stopLoading();
    }
  }, [currentPage, dispatch, keywordType, sort, startLoading, stopLoading]);

  /** 페이지 변경 */
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateQuery({
      params: {
        keywordType: selectedKeywordType.keyword,
        keyword: searchValue,
        page: page,
        size: 10,
        sort: sort,
      },
      route: '/order/orderList',
    });
  };

  /** 검색 value 변경 */
  const onChangeSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      searchValueRef.current = e.target.value;
    },
    []
  );

  /** 검색 아이콘 클릭 및 엔터 */
  const onClickSearch = useCallback(async () => {
    startLoading();
    try {
      const res = await getOrderList({
        keywordType: selectedKeywordType.keyword,
        keyword: searchValueRef.current,
        page: 1,
        size: 10,
      });
      if (res.isSuccess) {
        dispatch(order.setOrderList(res.data));
        updateQuery({
          params: {
            keywordType: selectedKeywordType.keyword,
            keyword: searchValueRef.current,
            page: 1,
            size: 10,
            sort: sort,
          },
          route: '/order/orderList',
        });
      }
    } finally {
      stopLoading();
    }
  }, [
    dispatch,
    selectedKeywordType.keyword,
    sort,
    startLoading,
    stopLoading,
    updateQuery,
  ]);

  /** 검색 초기화 */
  const onClickSearchReset = useCallback(() => {
    setCurrentPage(1);
    setSearchValue('');
    searchValueRef.current = '';
    fetchOrderList();
    navigate(langPath('/order/orderList'));
  }, [fetchOrderList, navigate]);

  /** Sort 선택 */
  const handleSortClick = useCallback(
    (type: ORDER_SORT_TYPE) => async () => {
      setCurrentPage(1);
      updateQuery({
        params: {
          keywordType: selectedKeywordType.keyword,
          keyword: searchValue,
          page: 1,
          size: 10,
          sort: type,
        },
        route: '/order/orderList',
      });
    },
    [searchValue, selectedKeywordType.keyword, updateQuery]
  );

  /** keywordType 저장 */
  const onDispatchKeyword = useCallback((keywordType: KeywordItemType) => {
    setSelectedKeywordType(keywordType);
  }, []);

  useEffect(() => {
    fetchOrderList();
  }, [fetchOrderList]);

  if (!orderList) return;
  return (
    <Container>
      <CommonTitle title={t('orderList')} imgUrl={Images.NavOrderListIcon} />
      <SearchWrap>
        <CommonSearch
          value={searchValue}
          onChange={onChangeSearch}
          onClickSearch={onClickSearch}
          onClickReset={onClickSearchReset}
          options={keywordTypeData}
          onSelect={onDispatchKeyword}
          selectedOption={selectedKeywordType}
        />
        <ListSorts
          handleSortClick={handleSortClick}
          orderListPage={true}
          count={orderList.totalElements}
          countName={t('orders')}
        />
      </SearchWrap>
      <OrderListItem items={orderList.items} />
      <Pagination
        totalItems={orderList.totalElements}
        itemsPerPage={10}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </Container>
  );
};

export default OrderListPage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background: ${colors.white};
  width: 100%;
  box-sizing: border-box;
  height: 100%;
  padding: 43.5px 0 0 13px;
`;

const SearchWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 45px;
  margin: 45px 0 12px;
`;
